import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import api from "../lib/api";
import { API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import {
  Receipt, Users, Calculator, ChartLine, CheckCircle, X, Plus, PencilSimple,
  Trash, Buildings, Target, Medal, Gear, HeartStraight
} from "@phosphor-icons/react";
import { RetentionTab } from "./RetentionTab";

const fmtInr = (v) => `₹${(Number(v) || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const STAGE_LABEL = {
  draft: "Draft",
  manager_approved: "Manager Approved",
  business_head_approved: "Business Head Approved",
  hr_approved: "HR Approved",
  finance_approved: "Finance Approved",
  paid: "Paid",
  rejected: "Rejected",
};
const STAGE_COLOR = {
  draft: "bg-slate-100 text-slate-700 border-slate-200",
  manager_approved: "bg-blue-50 text-blue-700 border-blue-200",
  business_head_approved: "bg-indigo-50 text-indigo-700 border-indigo-200",
  hr_approved: "bg-purple-50 text-purple-700 border-purple-200",
  finance_approved: "bg-amber-50 text-amber-700 border-amber-200",
  paid: "bg-green-50 text-green-700 border-green-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
};

const currentPeriod = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

// ---------- Shared helpers ----------
const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-xl border border-slate-200 shadow-sm ${className}`}>{children}</div>
);
const Btn = ({ children, onClick, variant = "primary", testId, disabled, className = "" }) => {
  const styles = {
    primary: "bg-blue-600 hover:bg-blue-700 text-white",
    secondary: "bg-slate-100 hover:bg-slate-200 text-slate-700",
    danger: "bg-red-600 hover:bg-red-700 text-white",
    success: "bg-green-600 hover:bg-green-700 text-white",
    ghost: "hover:bg-slate-100 text-slate-700",
  };
  return (
    <button
      data-testid={testId}
      disabled={disabled}
      onClick={onClick}
      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${styles[variant]} disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
    >
      {children}
    </button>
  );
};

// ==================== Plans Tab ====================
const PlansTab = ({ user }) => {
  const [plans, setPlans] = useState([]);
  const [editor, setEditor] = useState(null);
  const isAdmin = user.role === "admin";

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/incentive-srs/plans`);
      setPlans(data);
    } catch { toast.error("Failed to load plans"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const emptyPlan = () => ({
    plan_code: "", name: "", description: "", variable_pay_pct: 18,
    kpi_weights: [{ kpi_code: "pipeline", kpi_name: "Pipeline", weight: 40 }],
    slabs: [
      { min_pct: 0, max_pct: 50, payout_pct: 0, label: "Below" },
      { min_pct: 50, max_pct: 80, payout_pct: 50, label: "Partial" },
      { min_pct: 80, max_pct: 100, payout_pct: 100, label: "On target" },
      { min_pct: 100, max_pct: 120, payout_pct: 120, label: "Accelerator" },
      { min_pct: 120, max_pct: 10000, payout_pct: 150, label: "Super" },
    ],
    cap_pct: 150, quality_gate_threshold: 70, clawback_months: 6,
    role_applicable: ["bde"], active: true,
  });

  const savePlan = async () => {
    const isNew = !plans.find(p => p.plan_code === editor.plan_code && editor._orig);
    try {
      if (editor._orig) {
        await axios.put(`${API}/incentive-srs/plans/${editor._orig}?requester_role=${user.role}`, editor);
      } else {
        await axios.post(`${API}/incentive-srs/plans?requester_role=${user.role}`, editor);
      }
      toast.success(isNew ? "Plan created" : "Plan updated");
      setEditor(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const deletePlan = async (code) => {
    if (!window.confirm(`Delete plan ${code}?`)) return;
    await axios.delete(`${API}/incentive-srs/plans/${code}?requester_role=${user.role}`);
    toast.success("Deleted");
    load();
  };

  const seedDefaults = async () => {
    await axios.post(`${API}/incentive-srs/seed-defaults?requester_role=${user.role}`);
    toast.success("Default plans seeded");
    load();
  };

  const totalWeight = (editor?.kpi_weights || []).reduce((s, k) => s + Number(k.weight || 0), 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-800">Incentive Plans</h2>
        {isAdmin && (
          <div className="flex gap-2">
            <Btn variant="secondary" onClick={seedDefaults} testId="srs-seed-defaults">Seed Defaults</Btn>
            <Btn onClick={() => setEditor(emptyPlan())} testId="srs-new-plan"><Plus size={14} className="inline" /> New Plan</Btn>
          </div>
        )}
      </div>
      <Card className="overflow-hidden">
        <table className="w-full text-sm" data-testid="srs-plans-table">
          <thead className="bg-slate-50 text-left">
            <tr><th className="p-3">Code</th><th className="p-3">Name</th><th className="p-3">Var Pay %</th>
              <th className="p-3">Cap %</th><th className="p-3">Quality Gate</th><th className="p-3">KPIs</th>
              <th className="p-3">Roles</th><th className="p-3">Status</th>{isAdmin && <th className="p-3 text-right">Actions</th>}</tr>
          </thead>
          <tbody>
            {plans.map(p => (
              <tr key={p.plan_code} className="border-t hover:bg-slate-50">
                <td className="p-3 font-mono text-xs">{p.plan_code}</td>
                <td className="p-3 font-medium">{p.name}</td>
                <td className="p-3">{p.variable_pay_pct}%</td>
                <td className="p-3">{p.cap_pct}%</td>
                <td className="p-3">{p.quality_gate_threshold}</td>
                <td className="p-3 text-xs text-slate-600">{(p.kpi_weights || []).length}</td>
                <td className="p-3 text-xs">{(p.role_applicable || []).join(", ")}</td>
                <td className="p-3">
                  <span className={`text-xs px-2 py-1 rounded ${p.active ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                    {p.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                {isAdmin && (
                  <td className="p-3 text-right">
                    <button onClick={() => setEditor({ ...p, _orig: p.plan_code })} data-testid={`srs-edit-plan-${p.plan_code}`} className="text-blue-600 hover:bg-blue-50 p-1 rounded"><PencilSimple size={16} /></button>
                    <button onClick={() => deletePlan(p.plan_code)} data-testid={`srs-delete-plan-${p.plan_code}`} className="text-red-600 hover:bg-red-50 p-1 rounded ml-1"><Trash size={16} /></button>
                  </td>
                )}
              </tr>
            ))}
            {plans.length === 0 && (
              <tr><td colSpan={isAdmin ? 9 : 8} className="p-8 text-center text-slate-400">No plans. {isAdmin && "Click 'Seed Defaults' to start."}</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      {editor && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setEditor(null)}>
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-5 border-b flex items-center justify-between">
              <h3 className="text-lg font-bold">{editor._orig ? "Edit Plan" : "New Plan"}</h3>
              <button onClick={() => setEditor(null)} className="text-slate-500 hover:text-slate-800"><X size={20} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600">Plan Code</label>
                  <input data-testid="srs-plan-code" disabled={!!editor._orig} value={editor.plan_code} onChange={e => setEditor({ ...editor, plan_code: e.target.value.toUpperCase() })} className="w-full border rounded px-2 py-1.5 text-sm disabled:bg-slate-100" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Name</label>
                  <input data-testid="srs-plan-name" value={editor.name} onChange={e => setEditor({ ...editor, name: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Description</label>
                <textarea value={editor.description} onChange={e => setEditor({ ...editor, description: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" rows={2} />
              </div>
              <div className="grid grid-cols-4 gap-3">
                <div><label className="text-xs">Variable Pay %</label><input type="number" step="0.1" value={editor.variable_pay_pct} onChange={e => setEditor({ ...editor, variable_pay_pct: Number(e.target.value) })} className="w-full border rounded px-2 py-1.5 text-sm" /></div>
                <div><label className="text-xs">Cap %</label><input type="number" step="1" value={editor.cap_pct} onChange={e => setEditor({ ...editor, cap_pct: Number(e.target.value) })} className="w-full border rounded px-2 py-1.5 text-sm" /></div>
                <div><label className="text-xs">Quality Gate</label><input type="number" step="1" value={editor.quality_gate_threshold} onChange={e => setEditor({ ...editor, quality_gate_threshold: Number(e.target.value) })} className="w-full border rounded px-2 py-1.5 text-sm" /></div>
                <div><label className="text-xs">Clawback mo</label><input type="number" step="1" value={editor.clawback_months} onChange={e => setEditor({ ...editor, clawback_months: Number(e.target.value) })} className="w-full border rounded px-2 py-1.5 text-sm" /></div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Applicable Roles (comma-separated)</label>
                <input value={(editor.role_applicable || []).join(",")} onChange={e => setEditor({ ...editor, role_applicable: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} className="w-full border rounded px-2 py-1.5 text-sm" />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-sm">KPI Weights <span className={`ml-2 text-xs ${totalWeight === 100 ? 'text-green-600' : 'text-red-600'}`}>(Total: {totalWeight}%)</span></h4>
                  <button onClick={() => setEditor({ ...editor, kpi_weights: [...editor.kpi_weights, { kpi_code: "", kpi_name: "", weight: 0 }] })} className="text-xs text-blue-600"><Plus size={12} className="inline" /> Add KPI</button>
                </div>
                <div className="space-y-1">
                  {editor.kpi_weights.map((k, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center">
                      <input placeholder="code" value={k.kpi_code} onChange={e => { const nw = [...editor.kpi_weights]; nw[i].kpi_code = e.target.value; setEditor({ ...editor, kpi_weights: nw }); }} className="col-span-3 border rounded px-2 py-1 text-xs" />
                      <input placeholder="Name" value={k.kpi_name} onChange={e => { const nw = [...editor.kpi_weights]; nw[i].kpi_name = e.target.value; setEditor({ ...editor, kpi_weights: nw }); }} className="col-span-6 border rounded px-2 py-1 text-xs" />
                      <input type="number" placeholder="weight" value={k.weight} onChange={e => { const nw = [...editor.kpi_weights]; nw[i].weight = Number(e.target.value); setEditor({ ...editor, kpi_weights: nw }); }} className="col-span-2 border rounded px-2 py-1 text-xs" />
                      <button onClick={() => setEditor({ ...editor, kpi_weights: editor.kpi_weights.filter((_, ix) => ix !== i) })} className="col-span-1 text-red-600"><Trash size={14} /></button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-sm">Payout Slabs (Achievement % → Payout %)</h4>
                  <button onClick={() => setEditor({ ...editor, slabs: [...editor.slabs, { min_pct: 0, max_pct: 100, payout_pct: 100, label: "" }] })} className="text-xs text-blue-600"><Plus size={12} className="inline" /> Add Slab</button>
                </div>
                <div className="space-y-1">
                  {editor.slabs.map((s, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center">
                      <input type="number" placeholder="min%" value={s.min_pct} onChange={e => { const nw = [...editor.slabs]; nw[i].min_pct = Number(e.target.value); setEditor({ ...editor, slabs: nw }); }} className="col-span-2 border rounded px-2 py-1 text-xs" />
                      <input type="number" placeholder="max%" value={s.max_pct} onChange={e => { const nw = [...editor.slabs]; nw[i].max_pct = Number(e.target.value); setEditor({ ...editor, slabs: nw }); }} className="col-span-2 border rounded px-2 py-1 text-xs" />
                      <input type="number" placeholder="payout%" value={s.payout_pct} onChange={e => { const nw = [...editor.slabs]; nw[i].payout_pct = Number(e.target.value); setEditor({ ...editor, slabs: nw }); }} className="col-span-2 border rounded px-2 py-1 text-xs" />
                      <input placeholder="Label" value={s.label} onChange={e => { const nw = [...editor.slabs]; nw[i].label = e.target.value; setEditor({ ...editor, slabs: nw }); }} className="col-span-5 border rounded px-2 py-1 text-xs" />
                      <button onClick={() => setEditor({ ...editor, slabs: editor.slabs.filter((_, ix) => ix !== i) })} className="col-span-1 text-red-600"><Trash size={14} /></button>
                    </div>
                  ))}
                </div>
              </div>
              <label className="flex items-center gap-2"><input type="checkbox" checked={editor.active} onChange={e => setEditor({ ...editor, active: e.target.checked })} /> Active</label>
            </div>
            <div className="p-5 border-t flex justify-end gap-2">
              <Btn variant="secondary" onClick={() => setEditor(null)}>Cancel</Btn>
              <Btn onClick={savePlan} testId="srs-save-plan">Save Plan</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== Employees (HR) Tab ====================
const EmployeesTab = ({ user }) => {
  const [emps, setEmps] = useState([]);
  const [plans, setPlans] = useState([]);
  const [editor, setEditor] = useState(null);
  const load = useCallback(async () => {
    const [e, p] = await Promise.all([
      api.get(`${API}/incentive-srs/employees`),
      axios.get(`${API}/incentive-srs/plans`),
    ]);
    setEmps(e.data); setPlans(p.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const payload = { ...editor };
    delete payload.user_id; delete payload.email; delete payload.name; delete payload.role; delete payload.created_at;
    await api.put(`${API}/incentive-srs/employees/${editor.user_id}?requester_role=${user.role}`, payload);
    toast.success("Employee updated");
    setEditor(null);
    load();
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold text-slate-800">Employee Master (Incentive & Retention)</h2>
      <Card className="overflow-hidden">
        <table className="w-full text-sm" data-testid="srs-employees-table">
          <thead className="bg-slate-50 text-left text-xs">
            <tr><th className="p-2">Name</th><th className="p-2">Role</th><th className="p-2">Grade</th>
              <th className="p-2">DOJ</th><th className="p-2">Base Salary</th><th className="p-2">Plan</th>
              <th className="p-2">Var Pay?</th><th className="p-2">Approver Flags</th><th className="p-2">Critical</th><th className="p-2 text-right">Edit</th></tr>
          </thead>
          <tbody>
            {emps.map(e => (
              <tr key={e.user_id} className="border-t hover:bg-slate-50">
                <td className="p-2 font-medium">{e.name}<div className="text-xs text-slate-500">{e.email}</div></td>
                <td className="p-2"><span className="text-xs bg-slate-100 px-2 py-0.5 rounded">{e.role}</span></td>
                <td className="p-2 text-xs">{e.grade || "—"}</td>
                <td className="p-2 text-xs">{e.date_of_joining || "—"}</td>
                <td className="p-2">{e.base_salary ? fmtInr(e.base_salary) : "—"}</td>
                <td className="p-2 text-xs">{e.incentive_plan_code || "—"}</td>
                <td className="p-2 text-xs">{e.variable_pay_eligible ? "Yes" : "No"}</td>
                <td className="p-2 text-xs space-x-1">
                  {e.is_business_head && <span className="bg-indigo-50 text-indigo-700 px-1 rounded">BH</span>}
                  {e.is_hr && <span className="bg-purple-50 text-purple-700 px-1 rounded">HR</span>}
                  {e.is_finance && <span className="bg-amber-50 text-amber-700 px-1 rounded">FIN</span>}
                </td>
                <td className="p-2 text-xs">{e.critical_role_flag ? "⚠️ Yes" : "—"}</td>
                <td className="p-2 text-right">
                  <button onClick={() => setEditor({ ...e })} data-testid={`srs-edit-emp-${e.user_id}`} className="text-blue-600 hover:bg-blue-50 p-1 rounded"><PencilSimple size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {editor && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setEditor(null)}>
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-5 border-b flex items-center justify-between">
              <h3 className="text-lg font-bold">Edit Employee — {editor.name}</h3>
              <button onClick={() => setEditor(null)}><X size={20} /></button>
            </div>
            <div className="p-5 grid grid-cols-2 gap-3">
              {[
                ["employee_code", "Employee Code"],
                ["grade", "Grade / Level"],
                ["designation", "Designation"],
                ["department", "Department"],
                ["location", "Location"],
                ["date_of_joining", "Date of Joining (YYYY-MM-DD)"],
                ["last_increment_date", "Last Increment Date"],
                ["last_promotion_date", "Last Promotion Date"],
                ["employment_status", "Status (active/inactive/on_leave)"],
                ["mobile", "Mobile"],
              ].map(([k, lbl]) => (
                <div key={k}><label className="text-xs font-medium text-slate-600">{lbl}</label>
                  <input data-testid={`srs-emp-${k}`} value={editor[k] || ""} onChange={e => setEditor({ ...editor, [k]: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" />
                </div>
              ))}
              <div><label className="text-xs font-medium text-slate-600">Base Salary (₹/month)</label>
                <input type="number" data-testid="srs-emp-base_salary" value={editor.base_salary || 0} onChange={e => setEditor({ ...editor, base_salary: Number(e.target.value) })} className="w-full border rounded px-2 py-1.5 text-sm" /></div>
              <div><label className="text-xs font-medium text-slate-600">Incentive Plan</label>
                <select data-testid="srs-emp-plan" value={editor.incentive_plan_code || ""} onChange={e => setEditor({ ...editor, incentive_plan_code: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm">
                  <option value="">— None —</option>
                  {plans.map(p => <option key={p.plan_code} value={p.plan_code}>{p.plan_code} — {p.name}</option>)}
                </select></div>
              <div className="col-span-2 flex flex-wrap gap-4 pt-2 border-t">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editor.variable_pay_eligible} onChange={e => setEditor({ ...editor, variable_pay_eligible: e.target.checked })} /> Variable Pay Eligible</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editor.critical_role_flag} onChange={e => setEditor({ ...editor, critical_role_flag: e.target.checked })} /> Critical Role</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editor.is_business_head} onChange={e => setEditor({ ...editor, is_business_head: e.target.checked })} /> Business Head Approver</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editor.is_hr} onChange={e => setEditor({ ...editor, is_hr: e.target.checked })} /> HR Approver</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editor.is_finance} onChange={e => setEditor({ ...editor, is_finance: e.target.checked })} /> Finance Approver</label>
              </div>
            </div>
            <div className="p-5 border-t flex justify-end gap-2">
              <Btn variant="secondary" onClick={() => setEditor(null)}>Cancel</Btn>
              <Btn onClick={save} testId="srs-save-emp">Save</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== KPI Tab ====================
const KPITab = ({ user }) => {
  const [period, setPeriod] = useState(currentPeriod());
  const [emps, setEmps] = useState([]);
  const [plans, setPlans] = useState([]);
  const [selEmp, setSelEmp] = useState("");
  const [kpis, setKpis] = useState([]);
  const [newKpi, setNewKpi] = useState({ kpi_code: "", kpi_name: "", target: 0, achievement: 0, quality_score: 100, weight: 0 });

  const load = useCallback(async () => {
    const [e, p] = await Promise.all([
      api.get(`${API}/incentive-srs/employees`),
      axios.get(`${API}/incentive-srs/plans`),
    ]);
    setEmps(e.data.filter(u => u.role !== "customer")); setPlans(p.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadKpis = useCallback(async () => {
    if (!selEmp) return setKpis([]);
    const { data } = await axios.get(`${API}/incentive-srs/kpi?employee_id=${selEmp}&period=${period}`);
    setKpis(data);
  }, [selEmp, period]);
  useEffect(() => { loadKpis(); }, [loadKpis]);

  const emp = emps.find(e => e.user_id === selEmp);
  const plan = plans.find(p => p.plan_code === emp?.incentive_plan_code);

  const addKpi = async () => {
    if (!selEmp || !newKpi.kpi_code) return toast.error("Select employee & provide KPI code");
    await axios.post(`${API}/incentive-srs/kpi?requester_role=${user.role}`, {
      employee_id: selEmp, period, ...newKpi,
    });
    toast.success("KPI added");
    setNewKpi({ kpi_code: "", kpi_name: "", target: 0, achievement: 0, quality_score: 100, weight: 0 });
    loadKpis();
  };

  const delKpi = async (id) => {
    if (!window.confirm("Delete KPI?")) return;
    await axios.delete(`${API}/incentive-srs/kpi/${id}?requester_role=${user.role}`);
    loadKpis();
  };

  const fillFromPlan = () => {
    if (!plan) return toast.error("Employee has no plan assigned");
    const existing = new Set(kpis.map(k => k.kpi_code));
    const missing = plan.kpi_weights.filter(w => !existing.has(w.kpi_code));
    Promise.all(missing.map(w => axios.post(`${API}/incentive-srs/kpi?requester_role=${user.role}`, {
      employee_id: selEmp, period, kpi_code: w.kpi_code, kpi_name: w.kpi_name,
      target: 0, achievement: 0, quality_score: 100, weight: w.weight,
    }))).then(() => { toast.success(`Added ${missing.length} KPIs from plan`); loadKpis(); });
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold text-slate-800">KPI Capture</h2>
      <Card className="p-4">
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-xs font-medium text-slate-600">Period</label>
            <input type="month" value={period} onChange={e => setPeriod(e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" data-testid="srs-kpi-period" />
          </div>
          <div className="col-span-2">
            <label className="text-xs font-medium text-slate-600">Employee</label>
            <select value={selEmp} onChange={e => setSelEmp(e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" data-testid="srs-kpi-employee">
              <option value="">— Select —</option>
              {emps.map(e => <option key={e.user_id} value={e.user_id}>{e.name} ({e.role}) — Plan: {e.incentive_plan_code || 'none'}</option>)}
            </select>
          </div>
        </div>
        {selEmp && plan && (
          <div className="mt-3 flex items-center gap-3">
            <span className="text-xs text-slate-600">Plan: <b>{plan.name}</b> — Variable Pay {plan.variable_pay_pct}% of ₹{emp?.base_salary || 0}</span>
            <Btn variant="secondary" onClick={fillFromPlan} testId="srs-kpi-fill-plan">Auto-fill KPIs from plan</Btn>
          </div>
        )}
      </Card>

      {selEmp && (
        <Card className="overflow-hidden">
          <div className="p-3 border-b bg-slate-50 font-semibold text-sm">KPIs for {emp?.name} — {period}</div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs">
              <tr><th className="p-2">Code</th><th className="p-2">Name</th><th className="p-2">Weight %</th>
                <th className="p-2">Target</th><th className="p-2">Achievement</th><th className="p-2">Achv %</th>
                <th className="p-2">Quality</th><th className="p-2"></th></tr>
            </thead>
            <tbody>
              {kpis.map(k => (
                <tr key={k.kpi_id} className="border-t">
                  <td className="p-2 font-mono text-xs">{k.kpi_code}</td>
                  <td className="p-2">{k.kpi_name}</td>
                  <td className="p-2">{k.weight}%</td>
                  <td className="p-2">{k.target}</td>
                  <td className="p-2">{k.achievement}</td>
                  <td className="p-2 text-xs">{k.target ? Math.round(k.achievement / k.target * 100) : 0}%</td>
                  <td className="p-2">{k.quality_score}</td>
                  <td className="p-2 text-right"><button onClick={() => delKpi(k.kpi_id)} className="text-red-600" data-testid={`srs-del-kpi-${k.kpi_id}`}><Trash size={14} /></button></td>
                </tr>
              ))}
              <tr className="border-t bg-blue-50/30">
                <td className="p-2"><input placeholder="code" value={newKpi.kpi_code} onChange={e => setNewKpi({ ...newKpi, kpi_code: e.target.value })} className="w-full border rounded px-1 py-1 text-xs" data-testid="srs-new-kpi-code" /></td>
                <td className="p-2"><input placeholder="Name" value={newKpi.kpi_name} onChange={e => setNewKpi({ ...newKpi, kpi_name: e.target.value })} className="w-full border rounded px-1 py-1 text-xs" /></td>
                <td className="p-2"><input type="number" value={newKpi.weight} onChange={e => setNewKpi({ ...newKpi, weight: Number(e.target.value) })} className="w-full border rounded px-1 py-1 text-xs" /></td>
                <td className="p-2"><input type="number" value={newKpi.target} onChange={e => setNewKpi({ ...newKpi, target: Number(e.target.value) })} className="w-full border rounded px-1 py-1 text-xs" /></td>
                <td className="p-2"><input type="number" value={newKpi.achievement} onChange={e => setNewKpi({ ...newKpi, achievement: Number(e.target.value) })} className="w-full border rounded px-1 py-1 text-xs" data-testid="srs-new-kpi-achievement" /></td>
                <td className="p-2 text-xs text-slate-500">—</td>
                <td className="p-2"><input type="number" value={newKpi.quality_score} onChange={e => setNewKpi({ ...newKpi, quality_score: Number(e.target.value) })} className="w-full border rounded px-1 py-1 text-xs" /></td>
                <td className="p-2 text-right"><Btn onClick={addKpi} testId="srs-add-kpi" className="!px-2 !py-1"><Plus size={12} /></Btn></td>
              </tr>
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
};

// ==================== Calculate Tab ====================
const CalculateTab = ({ user }) => {
  const [period, setPeriod] = useState(currentPeriod());
  const [emps, setEmps] = useState([]);
  const [plans, setPlans] = useState([]);
  const [selEmp, setSelEmp] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get(`${API}/incentive-srs/employees`),
      axios.get(`${API}/incentive-srs/plans`),
    ]).then(([e, p]) => { setEmps(e.data.filter(u => u.role !== "customer")); setPlans(p.data); });
  }, []);

  const calc = async () => {
    if (!selEmp) return toast.error("Select employee");
    try {
      const { data } = await axios.post(`${API}/incentive-srs/calculate?requester_role=${user.role}`, { employee_id: selEmp, period });
      setResult(data);
      toast.success("Calculated!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const bulk = async () => {
    if (user.role !== "admin") return toast.error("Only admin");
    try {
      const { data } = await axios.post(`${API}/incentive-srs/calculate-bulk?requester_role=${user.role}`, { period });
      toast.success(`Bulk done — ${data.processed} employees processed`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold text-slate-800">Run Calculation</h2>
      <Card className="p-4">
        <div className="grid grid-cols-3 gap-3">
          <div><label className="text-xs">Period</label>
            <input type="month" value={period} onChange={e => setPeriod(e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" data-testid="srs-calc-period" /></div>
          <div className="col-span-2"><label className="text-xs">Employee</label>
            <select value={selEmp} onChange={e => setSelEmp(e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" data-testid="srs-calc-employee">
              <option value="">— Select —</option>
              {emps.map(e => <option key={e.user_id} value={e.user_id}>{e.name} ({e.role})</option>)}
            </select></div>
        </div>
        <div className="mt-3 flex gap-2">
          <Btn onClick={calc} testId="srs-run-calc"><Calculator size={14} className="inline mr-1" />Calculate</Btn>
          {user.role === "admin" && <Btn variant="secondary" onClick={bulk} testId="srs-run-bulk">Run Bulk (All eligible)</Btn>}
        </div>
      </Card>

      {result && (
        <Card className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-bold">Result — {result.calculation.plan_name}</h3>
              <p className="text-xs text-slate-500">Target Variable: {fmtInr(result.calculation.target_variable)}  |  Cap: {fmtInr(result.calculation.cap_applied)}</p>
            </div>
            <div className="text-right">
              <div className="text-xs text-slate-500">Final Amount</div>
              <div className={`text-2xl font-bold ${result.calculation.quality_gate_pass ? 'text-green-700' : 'text-red-600'}`}>{fmtInr(result.calculation.final_amount)}</div>
              <div className="text-xs">Quality Gate: <span className={result.calculation.quality_gate_pass ? 'text-green-700' : 'text-red-600'}>{result.calculation.avg_quality_score} / {result.calculation.quality_gate_threshold} {result.calculation.quality_gate_pass ? '✓' : '✗'}</span></div>
            </div>
          </div>
          <table className="w-full text-sm" data-testid="srs-calc-result">
            <thead className="bg-slate-50 text-left text-xs">
              <tr><th className="p-2">KPI</th><th className="p-2">Weight</th><th className="p-2">Target</th><th className="p-2">Achv</th><th className="p-2">Achv %</th><th className="p-2">Payout %</th><th className="p-2">Amount</th></tr>
            </thead>
            <tbody>
              {result.calculation.kpi_results.map((r) => (
                <tr key={r.kpi_code} className="border-t"><td className="p-2">{r.kpi_name}</td><td className="p-2">{r.weight}%</td>
                  <td className="p-2">{r.target}</td><td className="p-2">{r.achievement}</td>
                  <td className="p-2">{r.achievement_pct}%</td><td className="p-2">{r.payout_pct}%</td>
                  <td className="p-2 font-medium">{fmtInr(r.weighted_amount)}</td></tr>
              ))}
            </tbody>
          </table>
          <p className="mt-2 text-xs text-slate-500">Saved to ledger as draft. Move to next stage from the Approvals tab.</p>
        </Card>
      )}
    </div>
  );
};

// ==================== Ledger Tab ====================
const LedgerTab = ({ user }) => {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/incentive-srs/ledger?requester_role=${user.role}&requester_id=${user.user_id}`);
    setItems(data);
  }, [user.role, user.user_id]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold text-slate-800">Incentive Ledger</h2>
      <Card className="overflow-hidden">
        <table className="w-full text-sm" data-testid="srs-ledger-table">
          <thead className="bg-slate-50 text-left text-xs">
            <tr><th className="p-2">Employee</th><th className="p-2">Period</th><th className="p-2">Plan</th>
              <th className="p-2">Gross</th><th className="p-2">Final</th><th className="p-2">Quality</th>
              <th className="p-2">Status</th><th className="p-2"></th></tr>
          </thead>
          <tbody>
            {items.map(it => (
              <tr key={it.calc_id} className="border-t hover:bg-slate-50">
                <td className="p-2"><div className="font-medium">{it.employee_name}</div><div className="text-xs text-slate-500">{it.employee_role}</div></td>
                <td className="p-2">{it.period}</td><td className="p-2 text-xs">{it.plan_code}</td>
                <td className="p-2">{fmtInr(it.calculated_gross)}</td>
                <td className="p-2 font-medium">{fmtInr(it.final_amount)}</td>
                <td className="p-2 text-xs">{it.quality_gate_pass ? '✓' : '✗'} {it.avg_quality_score}</td>
                <td className="p-2"><span className={`text-xs px-2 py-0.5 rounded border ${STAGE_COLOR[it.status] || ''}`}>{STAGE_LABEL[it.status] || it.status}</span></td>
                <td className="p-2 text-right"><button onClick={() => setSelected(it)} data-testid={`srs-view-${it.calc_id}`} className="text-blue-600 text-xs underline">View</button></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-slate-400">No entries yet</td></tr>}
          </tbody>
        </table>
      </Card>
      {selected && <LedgerModal calc={selected} onClose={() => setSelected(null)} />}
    </div>
  );
};

const LedgerModal = ({ calc, onClose }) => (
  <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
    <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
      <div className="p-5 border-b flex items-center justify-between">
        <div><h3 className="text-lg font-bold">{calc.employee_name} — {calc.period}</h3>
          <p className="text-xs text-slate-500">{calc.plan_name} · {fmtInr(calc.final_amount)}</p></div>
        <button onClick={onClose}><X size={20} /></button>
      </div>
      <div className="p-5 space-y-3">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs">
            <tr><th className="p-2">KPI</th><th className="p-2">Wt</th><th className="p-2">Target</th><th className="p-2">Achv</th><th className="p-2">Achv %</th><th className="p-2">Payout %</th><th className="p-2">Amt</th></tr>
          </thead>
          <tbody>
            {calc.kpi_results.map((r) => (
              <tr key={r.kpi_code} className="border-t"><td className="p-2">{r.kpi_name}</td><td className="p-2">{r.weight}%</td>
                <td className="p-2">{r.target}</td><td className="p-2">{r.achievement}</td>
                <td className="p-2">{r.achievement_pct}%</td><td className="p-2">{r.payout_pct}%</td>
                <td className="p-2">{fmtInr(r.weighted_amount)}</td></tr>
            ))}
          </tbody>
        </table>
        <div>
          <h4 className="font-semibold mb-2">Approval History</h4>
          {(calc.approval_history || []).length === 0 && <p className="text-xs text-slate-400">No approvals yet.</p>}
          {(calc.approval_history || []).map((h) => (
            <div key={h.at} className="text-xs border-l-2 border-blue-400 pl-3 mb-2">
              <div><b>{h.from}</b> → <b>{h.to}</b> by {h.approver_name} ({h.approver_role})</div>
              <div className="text-slate-500">{h.at}</div>
              {h.notes && <div className="italic">"{h.notes}"</div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

// ==================== Approvals Tab ====================
const ApprovalsTab = ({ user }) => {
  const [items, setItems] = useState([]);
  const [userDoc, setUserDoc] = useState(null);
  const [noteFor, setNoteFor] = useState(null); // { calc, action }
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    const [pa, emps] = await Promise.all([
      axios.get(`${API}/incentive-srs/pending-approvals?requester_role=${user.role}&requester_id=${user.user_id}`),
      api.get(`${API}/incentive-srs/employees`),
    ]);
    setItems(pa.data);
    setUserDoc(emps.data.find(e => e.user_id === user.user_id) || { role: user.role });
  }, [user.role, user.user_id]);
  useEffect(() => { load(); }, [load]);

  const doAction = async (action) => {
    try {
      const payload = {
        approver_id: user.user_id,
        approver_name: user.name || "",
        approver_role: user.role,
        approver_is_business_head: !!userDoc?.is_business_head,
        approver_is_hr: !!userDoc?.is_hr,
        approver_is_finance: !!userDoc?.is_finance,
        notes: note,
      };
      const url = `${API}/incentive-srs/${action === "approve" ? "approve" : "reject"}/${noteFor.calc.calc_id}`;
      await axios.post(url, payload);
      toast.success(action === "approve" ? "Approved & notified" : "Rejected & notified");
      setNoteFor(null); setNote("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-800">Pending Approvals</h2>
        <div className="text-xs text-slate-500">Alerts go to <b>info@swiftechindia.com</b></div>
      </div>
      <Card className="overflow-hidden">
        <table className="w-full text-sm" data-testid="srs-approvals-table">
          <thead className="bg-slate-50 text-left text-xs">
            <tr><th className="p-2">Employee</th><th className="p-2">Period</th><th className="p-2">Amount</th>
              <th className="p-2">Quality</th><th className="p-2">Current Stage</th><th className="p-2 text-right">Action</th></tr>
          </thead>
          <tbody>
            {items.map(it => (
              <tr key={it.calc_id} className="border-t hover:bg-slate-50">
                <td className="p-2"><b>{it.employee_name}</b> <span className="text-xs text-slate-500">({it.employee_role})</span></td>
                <td className="p-2">{it.period}</td>
                <td className="p-2 font-semibold">{fmtInr(it.final_amount)}</td>
                <td className="p-2 text-xs">{it.quality_gate_pass ? '✓' : '✗'} {it.avg_quality_score}</td>
                <td className="p-2"><span className={`text-xs px-2 py-0.5 rounded border ${STAGE_COLOR[it.status] || ''}`}>{STAGE_LABEL[it.status]}</span></td>
                <td className="p-2 text-right space-x-1">
                  <Btn variant="success" onClick={() => { setNoteFor({ calc: it, action: "approve" }); setNote(""); }} testId={`srs-approve-${it.calc_id}`}><CheckCircle size={12} className="inline" /> Approve</Btn>
                  <Btn variant="danger" onClick={() => { setNoteFor({ calc: it, action: "reject" }); setNote(""); }} testId={`srs-reject-${it.calc_id}`}><X size={12} className="inline" /> Reject</Btn>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-400">No items pending your approval</td></tr>}
          </tbody>
        </table>
      </Card>

      {noteFor && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setNoteFor(null)}>
          <div className="bg-white rounded-xl max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="p-5 border-b"><h3 className="font-bold">{noteFor.action === "approve" ? "Approve" : "Reject"} — {noteFor.calc.employee_name}</h3></div>
            <div className="p-5">
              <label className="text-xs">Notes {noteFor.action === "reject" && <span className="text-red-500">(reason)</span>}</label>
              <textarea value={note} onChange={e => setNote(e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" rows={4} data-testid="srs-approval-note" />
            </div>
            <div className="p-5 border-t flex justify-end gap-2">
              <Btn variant="secondary" onClick={() => setNoteFor(null)}>Cancel</Btn>
              <Btn variant={noteFor.action === "approve" ? "success" : "danger"} onClick={() => doAction(noteFor.action)} testId="srs-confirm-action">
                Confirm {noteFor.action}
              </Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== Main ====================
const IncentiveSRS = ({ user, logout }) => {
  const role = user?.role || "bde";
  const TABS = useMemo(() => {
    const t = [{ key: "ledger", label: "Ledger", icon: Receipt }];
    if (["admin", "manager", "bdm", "cfo"].includes(role)) t.unshift({ key: "approvals", label: "Approvals", icon: CheckCircle });
    if (["admin", "manager", "bdm"].includes(role)) {
      t.push({ key: "calculate", label: "Calculate", icon: Calculator });
      t.push({ key: "kpi", label: "KPI", icon: Target });
    }
    // Retention visible to all non-customer roles (BDE sees own score only)
    t.push({ key: "retention", label: "Retention", icon: HeartStraight });
    if (role === "admin") {
      t.push({ key: "employees", label: "Employees", icon: Users });
      t.push({ key: "plans", label: "Plans", icon: Gear });
    }
    return t;
  }, [role]);
  const [tab, setTab] = useState(TABS[0].key);

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      <div className="flex-1 p-6 ml-64 max-w-full overflow-hidden">
        <div className="mb-6">
          <div className="flex items-center gap-2 text-sm text-slate-500 mb-1"><Medal size={16} /> Incentive & Retention (SRS)</div>
          <h1 className="text-3xl font-bold text-slate-800">Incentive System</h1>
          <p className="text-sm text-slate-500">KPI-driven incentive engine with multi-level approvals. Email alerts → info@swiftechindia.com</p>
        </div>
        <div className="flex gap-1 border-b border-slate-200 mb-4 overflow-x-auto">
          {TABS.map(t => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`srs-tab-${t.key}`}
                className={`px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition ${tab === t.key ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
              >
                <Icon size={14} className="inline mr-1" />{t.label}
              </button>
            );
          })}
        </div>
        <div data-testid="srs-tab-content">
          {tab === "approvals" && <ApprovalsTab user={user} />}
          {tab === "ledger" && <LedgerTab user={user} />}
          {tab === "calculate" && <CalculateTab user={user} />}
          {tab === "kpi" && <KPITab user={user} />}
          {tab === "retention" && <RetentionTab user={user} />}
          {tab === "employees" && <EmployeesTab user={user} />}
          {tab === "plans" && <PlansTab user={user} />}
        </div>
      </div>
    </div>
  );
};

export default IncentiveSRS;
